import React from 'react';
import { Repeat } from 'lucide-react';
import {
  RequestStatusBadge,
  StatusBadgeWithLoader,
} from 'src/features/requests/request-status-badge';
import {
  isInReeditCycle,
  resolveDisplayedRequestStatus,
} from 'src/features/requests/request.helper';
import { requestStatus, RequestsTableResponse } from 'src/lib/services/api/request-api';
import {
  UploadingStatusCell,
  useBackgroundUploadStore,
} from 'src/features/requests/background-upload';

type Props = {
  request: Pick<
    RequestsTableResponse,
    | 'id'
    | 'status'
    | 'requested_attention_at'
    | 'requested_reedit_at'
    | 'reedit_count'
    | 'approved_at'
    | 'revisions_count'
    | 'rated_revisions_count'
  >;
  size?: 'sm' | 'default';
};

/**
 * Renders the status pill for a request row.
 *
 * Render paths (highest priority first):
 *  1. status === 'uploading' → {@link UploadingStatusCell} (progress + retry).
 *  2. status === 'queued' AND a background upload is still in flight for
 *     this request → Queued pill with a Loader2 icon INSIDE the pill so
 *     the user sees the upload is still progressing even after the
 *     status flipped to queued. (Karina #2372 follow-up.)
 *  3. Otherwise → derived pill via `resolveDisplayedRequestStatus`,
 *     which overrides with `needs_attention` or `needs_reedit` when the
 *     row's flags are set. While the row is in the re-edit cycle a
 *     static `Repeat` icon trails inside the pill (`Needs Re-edit →
 *     Editing → Ready for review`) so the "still in cycle" signal
 *     persists across status flips. (#2383, #2442.) It's static rather
 *     than a spinner because it signals "went through a re-edit cycle",
 *     not "loading" — a spinning icon on a "Ready for review" pill read
 *     to clients as the request being stuck (#164).
 *
 * Re-edit icon is suppressed when the displayed pill is `needs_attention`.
 * The Needs Attention overlay is urgency-first and supersedes the re-edit
 * pill: stacking the re-edit icon on top of an attention pill reads as
 * "Needs Attention is in re-edit" — nonsense to the user (#2462 Karina).
 * The underlying cycle state is unchanged; once attention is resolved the
 * pill drops back to its underlying status and the icon resumes naturally
 * if a re-edit is still in flight.
 */
const RequestStatusCell: React.FC<Props> = ({ request, size = 'sm' }) => {
  const { id: requestId, status } = request;
  const hasActiveBackgroundUpload = useBackgroundUploadStore(
    (s) => !!s.activeRequests[requestId],
  );

  if (status === requestStatus.uploading) {
    return <UploadingStatusCell requestId={requestId} size={size} />;
  }

  if (status === requestStatus.queued && hasActiveBackgroundUpload) {
    return <StatusBadgeWithLoader size={size} variant={status} />;
  }

  const displayedStatus = resolveDisplayedRequestStatus(request);
  const showReeditIndicator =
    isInReeditCycle(request) && displayedStatus !== 'needs_attention';

  return (
    <RequestStatusBadge
      size={size}
      variant={displayedStatus}
      iconPrefix={
        showReeditIndicator ? (
          <Repeat size={12} className={'tw-shrink-0'} aria-hidden={'true'} />
        ) : undefined
      }
    />
  );
};

export { RequestStatusCell };
